import React, { useEffect, useMemo, useRef, useState } from 'react';
import type {
  CurationCandidate,
  Entitlement,
  HighlightJob,
  RefineClip,
  RefineMusicOptions,
} from '../api/types';
import { getMusicManifest } from '../api/rttsClient';
import { isRecapEditorV2Enabled, isStudioUseMock, STUDIO_COPY } from '../config';
import CustomizeMusicPanel from '../music/CustomizeMusicPanel';
import {
  defaultGameGainDb,
  defaultMusicGainDb,
  gainDbToLinearVolume,
  loadBundledTracks,
} from '../music/loadTracks';
import {
  DEFAULT_MUSIC_VOLUME,
  DEFAULT_REEL_AUDIO_VOLUME,
  initialMusicVolume,
  initialReelAudioVolume,
  initialVoiceVolume,
  volumePercentToGainDb,
  volumePercentToLinear,
} from '../music/addonVolume';
import { fetchMusicPreviewObjectUrl } from '../music/previewAudio';
import type { AddonMixVolumes } from './AddOnsPanel';
import type { MusicMode, MusicTrack } from '../music/types';
import {
  DEFAULT_MUSIC_FADE_IN_S,
  DEFAULT_MUSIC_FADE_OUT_S,
  DEFAULT_MUSIC_LOOP,
} from '../music/types';
import AdjustedReelPlayer, {
  type AdjustedReelPlayerHandle,
} from './AdjustedReelPlayer';
import CandidateClipPreview from './CandidateClipPreview';
import ClipSegmentPlayer, {
  type ClipSegment,
  type ClipSegmentPlayerHandle,
} from './ClipSegmentPlayer';
import CustomizeHowItWorks from './CustomizeHowItWorks';
import CustomizePickStep from './CustomizePickStep';
import CustomizeTimeline from './CustomizeTimeline';
import RecapClipStrip from './RecapClipStrip';
import RecapEditorV2 from './RecapEditorV2';
import AddOnsPanel from './AddOnsPanel';
import { RECAP_EDITOR_V2_PHASE0_MIX_CARRY } from './recapEditorV2Model';
import {
  arrangementOffsetSec,
  buildRefineClips,
  clipsFingerprint,
  formatTotalClock,
  initialBounds,
  insertChronologically,
  seedOrderedIds,
  trimRails,
  type ClipBounds,
  clampBounds,
} from './customizeHelpers';

export type { RefineMusicOptions };

const NUDGE_SEC = 0.5;

function seedMusicOptions(seed?: RefineMusicOptions | null): RefineMusicOptions {
  if (seed && typeof seed === 'object') {
    return {
      music_id: seed.music_id ?? null,
      music_mode:
        seed.music_mode === 'music_only' ? 'music_only' : 'bed',
      game_gain_db:
        typeof seed.game_gain_db === 'number'
          ? seed.game_gain_db
          : defaultGameGainDb(),
      music_gain_db:
        typeof seed.music_gain_db === 'number'
          ? seed.music_gain_db
          : defaultMusicGainDb(),
      music_loop:
        typeof seed.music_loop === 'boolean'
          ? seed.music_loop
          : DEFAULT_MUSIC_LOOP,
      music_fade_in_s:
        typeof seed.music_fade_in_s === 'number'
          ? seed.music_fade_in_s
          : DEFAULT_MUSIC_FADE_IN_S,
      music_fade_out_s:
        typeof seed.music_fade_out_s === 'number'
          ? seed.music_fade_out_s
          : DEFAULT_MUSIC_FADE_OUT_S,
    };
  }
  return {
    music_id: null,
    music_mode: 'bed',
    game_gain_db: defaultGameGainDb(),
    music_gain_db: defaultMusicGainDb(),
    music_loop: DEFAULT_MUSIC_LOOP,
    music_fade_in_s: DEFAULT_MUSIC_FADE_IN_S,
    music_fade_out_s: DEFAULT_MUSIC_FADE_OUT_S,
  };
}

type RefineReviewPanelProps = {
  candidates: CurationCandidate[];
  submitting?: boolean;
  error?: string | null;
  onSubmit: (clips: RefineClip[], music?: RefineMusicOptions | null) => void;
  onCancel: () => void;
  sourcePreviewUrl?: string | null;
  sourcePreviewExpiresAt?: string | null;
  onRefreshPreview?: () => void | Promise<void>;
  refreshingPreview?: boolean;
  reelPreviewUrl?: string | null;
  rebuildingReel?: boolean;
  reelPreviewError?: string | null;
  curateEnabled?: boolean;
  maxClips?: number;
  maxDurationSec?: number;
  acceptEnabled?: boolean;
  acceptBusy?: boolean;
  acceptError?: string | null;
  acceptCostHint?: string | null;
  onApprove?: (
    clips: RefineClip[],
    meta: { dirty: boolean },
    music?: RefineMusicOptions | null
  ) => void;
  /** Standalone music picker (Step 2) — REACT_APP_RTTS_MUSIC_ENABLED. */
  musicEnabled?: boolean;
  /** Seed from working job music_* / gain fields. */
  initialMusic?: RefineMusicOptions | null;
  /** V2 shell only — existing job/addons/name so inspector reuses AddOnsPanel. */
  job?: HighlightJob | null;
  entitlement?: Entitlement | null;
  videoRef?: React.RefObject<HTMLVideoElement | null>;
  recordDock?: HTMLElement | null;
  onRecordingChange?: (recording: boolean) => void;
  onJobUpdated?: (job: HighlightJob) => void;
  reelName?: string;
  onReelNameChange?: (next: string) => void;
};

function isPreviewExpired(expiresAt: string | null | undefined): boolean {
  if (!expiresAt) return false;
  const ms = Date.parse(expiresAt);
  if (Number.isNaN(ms)) return false;
  return Date.now() >= ms;
}

/** Trim-only path (curate flag off): keep the existing list + nudge UI. */
const TrimOnlyPanel: React.FC<RefineReviewPanelProps> = (props) => {
  const {
    candidates,
    submitting = false,
    error = null,
    onSubmit,
    onCancel,
    sourcePreviewUrl = null,
    sourcePreviewExpiresAt = null,
    onRefreshPreview,
    refreshingPreview = false,
    reelPreviewUrl = null,
    rebuildingReel = false,
    reelPreviewError = null,
    acceptEnabled = false,
    acceptBusy = false,
    acceptError = null,
    acceptCostHint = null,
    onApprove,
  } = props;

  const seed = useMemo(() => {
    const selected = new Set<string>();
    const bounds: Record<string, ClipBounds> = {};
    for (const c of candidates) {
      bounds[c.candidate_id] = initialBounds(c);
      if (c.selected_by_default || c.in_current_reel || c.in_reel) {
        selected.add(c.candidate_id);
      }
    }
    if (selected.size === 0) {
      for (const c of candidates) selected.add(c.candidate_id);
    }
    return { selected, bounds };
  }, [candidates]);

  const [selected, setSelected] = useState(seed.selected);
  const [bounds, setBounds] = useState(seed.bounds);
  const [baseline, setBaseline] = useState(() =>
    clipsFingerprint([...seed.selected], seed.bounds)
  );
  const [activeSegment, setActiveSegment] = useState<ClipSegment | null>(null);
  const playGenRef = useRef(0);
  const playerRef = useRef<ClipSegmentPlayerHandle | null>(null);
  const wasRebuildingRef = useRef(false);

  const byId = useMemo(() => {
    const map = new Map<string, CurationCandidate>();
    for (const c of candidates) map.set(c.candidate_id, c);
    return map;
  }, [candidates]);

  const orderedIds = useMemo(
    () =>
      candidates
        .filter((c) => selected.has(c.candidate_id))
        .sort((a, b) => {
          const ba = bounds[a.candidate_id] || initialBounds(a);
          const bb = bounds[b.candidate_id] || initialBounds(b);
          return ba.start - bb.start;
        })
        .map((c) => c.candidate_id),
    [candidates, selected, bounds]
  );

  const dirty = clipsFingerprint(orderedIds, bounds) !== baseline;
  const busy = submitting || rebuildingReel || acceptBusy;
  const previewAvailable =
    Boolean(sourcePreviewUrl) && !isPreviewExpired(sourcePreviewExpiresAt);

  useEffect(() => {
    if (wasRebuildingRef.current && !rebuildingReel && !reelPreviewError) {
      setBaseline(clipsFingerprint(orderedIds, bounds));
    }
    wasRebuildingRef.current = rebuildingReel;
  }, [rebuildingReel, reelPreviewError, orderedIds, bounds]);

  const previewClip = (c: CurationCandidate) => {
    const b = bounds[c.candidate_id] || initialBounds(c);
    playGenRef.current += 1;
    const next: ClipSegment = {
      id: c.candidate_id,
      label: c.label,
      startSec: b.start,
      endSec: b.end,
      playKey: playGenRef.current,
    };
    setActiveSegment(next);
    playerRef.current?.playNow(next);
  };

  const clips = () => buildRefineClips(orderedIds, bounds, byId);

  return (
    <div className="studio-customize-shell" data-testid="studio-refine">
      <div className="space-y-2 text-center shrink-0 px-1">
        <h1 className="text-2xl md:text-3xl font-semibold">
          {STUDIO_COPY.refineHeadline}
        </h1>
        <p className="text-sm text-[var(--studio-muted)]">{STUDIO_COPY.refineBlurb}</p>
      </div>
      <div className="studio-customize-layout">
        <aside className="studio-customize-player-dock space-y-3">
          <ClipSegmentPlayer
            ref={playerRef}
            sourceUrl={sourcePreviewUrl}
            sourceExpiresAt={sourcePreviewExpiresAt}
            segment={activeSegment}
            onRefreshSource={onRefreshPreview}
            refreshingSource={refreshingPreview}
          />
          <AdjustedReelPlayer
            reelUrl={reelPreviewUrl}
            rebuilding={rebuildingReel}
            error={reelPreviewError}
          />
        </aside>
        <ul className="studio-customize-clips space-y-3" data-testid="studio-refine-list">
          {candidates.map((c) => {
            const kept = selected.has(c.candidate_id);
            const b = bounds[c.candidate_id] || initialBounds(c);
            return (
              <li
                key={c.candidate_id}
                className="rounded-lg border border-white/15 bg-white/[0.03] p-4 space-y-3"
                data-testid={`studio-refine-clip-${c.candidate_id}`}
              >
                <div className="flex justify-between gap-3">
                  <p className="font-medium truncate">{c.label}</p>
                  <button
                    type="button"
                    className="studio-btn-ghost text-sm"
                    data-testid={`studio-refine-drop-${c.candidate_id}`}
                    disabled={busy || (kept && selected.size <= 1)}
                    onClick={() => {
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(c.candidate_id)) {
                          if (next.size <= 1) return prev;
                          next.delete(c.candidate_id);
                        } else next.add(c.candidate_id);
                        return next;
                      });
                    }}
                  >
                    {kept ? STUDIO_COPY.refineDrop : STUDIO_COPY.refineKeep}
                  </button>
                </div>
                <CandidateClipPreview
                  candidate={c}
                  previewAvailable={previewAvailable}
                  onPreview={previewClip}
                  isActive={activeSegment?.id === c.candidate_id}
                />
                {kept ? (
                  <div className="flex flex-wrap gap-2">
                    {(['start', 'end'] as const).flatMap((edge) =>
                      [-NUDGE_SEC, NUDGE_SEC].map((delta) => (
                        <button
                          key={`${edge}-${delta}`}
                          type="button"
                          className="studio-btn-ghost text-xs"
                          disabled={busy}
                          onClick={() => {
                            const rails = trimRails(c);
                            const cur = bounds[c.candidate_id] || initialBounds(c);
                            const start =
                              edge === 'start' ? cur.start + delta : cur.start;
                            const end =
                              edge === 'end' ? cur.end + delta : cur.end;
                            const next = clampBounds(rails, start, end);
                            setBounds((prev) => ({
                              ...prev,
                              [c.candidate_id]: next,
                            }));
                          }}
                        >
                          {edge === 'start'
                            ? delta < 0
                              ? STUDIO_COPY.refineTrimStartEarlier
                              : STUDIO_COPY.refineTrimStartLater
                            : delta < 0
                              ? STUDIO_COPY.refineTrimEndEarlier
                              : STUDIO_COPY.refineTrimEndLater}
                        </button>
                      ))
                    )}
                  </div>
                ) : null}
                <p className="text-xs text-[var(--studio-muted)]">
                  {b.start.toFixed(1)}s → {b.end.toFixed(1)}s
                </p>
              </li>
            );
          })}
        </ul>
      </div>
      {error ? (
        <p className="text-sm text-center text-[var(--studio-danger)]" role="alert">
          {error}
        </p>
      ) : null}
      {acceptError ? (
        <p
          className="text-sm text-center text-[var(--studio-danger)]"
          role="alert"
          data-testid="studio-customize-accept-error"
        >
          {acceptError}
        </p>
      ) : null}
      <div className="studio-customize-actions flex flex-col items-center gap-3">
        {acceptEnabled && onApprove ? (
          <button
            type="button"
            className="studio-btn-primary"
            data-testid="studio-customize-approve"
            disabled={busy || orderedIds.length < 1}
            onClick={() => onApprove(clips(), { dirty })}
          >
            {acceptBusy
              ? STUDIO_COPY.acceptCtaBusy
              : STUDIO_COPY.acceptCtaWithCost}
          </button>
        ) : null}
        <div className="flex flex-wrap gap-3 justify-center">
          {(dirty || !acceptEnabled) && (
            <button
              type="button"
              className={acceptEnabled ? 'studio-btn-ghost' : 'studio-btn-primary'}
              data-testid="studio-refine-submit"
              disabled={busy || orderedIds.length < 1}
              onClick={() => onSubmit(clips())}
            >
              {submitting || rebuildingReel
                ? STUDIO_COPY.refineBusy
                : STUDIO_COPY.refineSubmit}
            </button>
          )}
          <button
            type="button"
            className="studio-btn-ghost"
            data-testid="studio-refine-cancel"
            disabled={busy}
            onClick={onCancel}
          >
            {dirty ? STUDIO_COPY.refineCancelDiscard : STUDIO_COPY.refineCancel}
          </button>
        </div>
        {acceptCostHint && acceptEnabled ? (
          <p className="text-xs text-[var(--studio-muted)]">{acceptCostHint}</p>
        ) : null}
      </div>
    </div>
  );
};

const RefineReviewPanel: React.FC<RefineReviewPanelProps> = (props) => {
  const {
    candidates,
    submitting = false,
    error = null,
    onSubmit,
    onCancel,
    sourcePreviewUrl = null,
    sourcePreviewExpiresAt = null,
    onRefreshPreview,
    refreshingPreview = false,
    reelPreviewUrl = null,
    rebuildingReel = false,
    reelPreviewError = null,
    curateEnabled = false,
    maxClips = 20,
    maxDurationSec = 180,
    acceptEnabled = false,
    acceptBusy = false,
    acceptError = null,
    acceptCostHint = null,
    onApprove,
    musicEnabled = false,
    initialMusic = null,
    job = null,
    entitlement = null,
    videoRef,
    recordDock = null,
    onRecordingChange,
    onJobUpdated,
    reelName,
    onReelNameChange,
  } = props;

  if (!curateEnabled) {
    return <TrimOnlyPanel {...props} />;
  }

  const seedBounds = useMemo(() => {
    const bounds: Record<string, ClipBounds> = {};
    for (const c of candidates) bounds[c.candidate_id] = initialBounds(c);
    return bounds;
  }, [candidates]);

  const [orderedIds, setOrderedIds] = useState(() => seedOrderedIds(candidates));
  const [bounds, setBounds] = useState(seedBounds);
  const [baseline, setBaseline] = useState(() =>
    clipsFingerprint(seedOrderedIds(candidates), seedBounds)
  );
  const [step, setStep] = useState<1 | 2>(1);
  const [playerMode, setPlayerMode] = useState<'clip' | 'reel'>('clip');
  /** Instant client playlist vs watermarked server render inside Adjusted reel. */
  const [reelSource, setReelSource] = useState<'instant' | 'watermarked'>(
    'instant'
  );
  const [playlistPlayKey, setPlaylistPlayKey] = useState(0);
  const [activeSegment, setActiveSegment] = useState<ClipSegment | null>(null);
  const [musicTracks, setMusicTracks] = useState<MusicTrack[]>(() =>
    musicEnabled ? loadBundledTracks() : []
  );
  const [music, setMusic] = useState<RefineMusicOptions>(() =>
    seedMusicOptions(initialMusic)
  );
  const [instantMusicUrl, setInstantMusicUrl] = useState<string | null>(null);
  const [autoArrange, setAutoArrange] = useState(true);
  const [snap, setSnap] = useState(true);
  const playGenRef = useRef(0);
  const playerRef = useRef<ClipSegmentPlayerHandle | null>(null);
  const adjustedPlayerRef = useRef<AdjustedReelPlayerHandle | null>(null);
  const wasRebuildingRef = useRef(false);
  const [playheadSec, setPlayheadSec] = useState(0);
  const [playheadDur, setPlayheadDur] = useState(0);
  const [v2Recording, setV2Recording] = useState(false);
  const [v2RecordDock, setV2RecordDock] = useState<HTMLDivElement | null>(null);
  const [mixVolumes, setMixVolumes] = useState<AddonMixVolumes>(() => ({
    music_volume: initialMusicVolume(job || {}),
    reel_audio_volume: initialReelAudioVolume(job || {}),
    voice_volume: initialVoiceVolume(job || {}),
  }));
  const v2VideoRef = useMemo<React.RefObject<HTMLVideoElement | null>>(
    () => ({
      get current() {
        const staged =
          playerMode === 'reel' && reelSource === 'watermarked'
            ? adjustedPlayerRef.current?.getVideo() ?? null
            : playerRef.current?.getVideo() ?? null;
        return staged || videoRef?.current || null;
      },
    }),
    [playerMode, reelSource, videoRef]
  );

  useEffect(() => {
    if (!musicEnabled) return;
    let cancelled = false;
    void getMusicManifest()
      .then((payload) => {
        if (!cancelled && payload.tracks.length > 0) {
          setMusicTracks((prev) => {
            if (
              prev.length === payload.tracks.length &&
              prev.every((t, i) => t.id === payload.tracks[i]?.id)
            ) {
              return prev;
            }
            return payload.tracks;
          });
        }
      })
      .catch(() => {
        // Bundled catalog already seeded.
      });
    return () => {
      cancelled = true;
    };
  }, [musicEnabled]);

  // Session-gated music bed for instant Adjusted-reel mix (blob URL; never logged).
  useEffect(() => {
    if (!musicEnabled || !music.music_id) {
      setInstantMusicUrl(null);
      return;
    }
    let cancelled = false;
    const track = musicTracks.find((t) => t.id === music.music_id);
    void fetchMusicPreviewObjectUrl(music.music_id, {
      previewUrl: track?.preview_url,
      useMock: isStudioUseMock(),
    })
      .then((url) => {
        if (!cancelled) setInstantMusicUrl(url);
      })
      .catch(() => {
        if (!cancelled) setInstantMusicUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [musicEnabled, music.music_id, musicTracks]);

  // Restart instant reel when the selected track changes while arrangement preview is live.
  useEffect(() => {
    if (playerMode === 'reel' && reelSource === 'instant') {
      setPlaylistPlayKey((k) => k + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- music_id only
  }, [music.music_id]);

  const byId = useMemo(() => {
    const map = new Map<string, CurationCandidate>();
    for (const c of candidates) map.set(c.candidate_id, c);
    return map;
  }, [candidates]);

  const totalDuration = orderedIds.reduce((sum, id) => {
    const b = bounds[id];
    return sum + (b ? Math.max(0, b.end - b.start) : 0);
  }, 0);
  const overClipCap = orderedIds.length > maxClips;
  const overDurationCap = totalDuration > maxDurationSec;
  const dirty = clipsFingerprint(orderedIds, bounds) !== baseline;
  const busy = submitting || rebuildingReel || acceptBusy;
  const previewAvailable =
    Boolean(sourcePreviewUrl) && !isPreviewExpired(sourcePreviewExpiresAt);

  useEffect(() => {
    if (wasRebuildingRef.current && !rebuildingReel && !reelPreviewError) {
      setBaseline(clipsFingerprint(orderedIds, bounds));
      if (reelPreviewUrl) {
        setPlayerMode('reel');
        setReelSource('watermarked');
      }
    }
    wasRebuildingRef.current = rebuildingReel;
  }, [rebuildingReel, reelPreviewError, reelPreviewUrl, orderedIds, bounds]);

  const playlist = useMemo((): ClipSegment[] => {
    return orderedIds
      .map((id) => {
        const c = byId.get(id);
        const b = bounds[id];
        if (!c || !b) return null;
        return {
          id,
          label: c.label,
          startSec: b.start,
          endSec: b.end,
          playKey: 0,
        };
      })
      .filter((s): s is ClipSegment => Boolean(s));
  }, [orderedIds, bounds, byId]);

  const showInstantReel = () => {
    setPlayerMode('reel');
    setReelSource('instant');
    setPlaylistPlayKey((k) => k + 1);
  };

  const playId = (id: string) => {
    const c = byId.get(id);
    const b = bounds[id];
    if (!c || !b) return;
    playGenRef.current += 1;
    const next: ClipSegment = {
      id,
      label: c.label,
      startSec: b.start,
      endSec: b.end,
      playKey: playGenRef.current,
    };
    setPlayerMode('clip');
    setActiveSegment(next);
    if (previewAvailable) playerRef.current?.playNow(next);
  };

  const previewCandidate = (c: CurationCandidate) => {
    const b = bounds[c.candidate_id] || initialBounds(c);
    playGenRef.current += 1;
    const next: ClipSegment = {
      id: c.candidate_id,
      label: c.label,
      startSec: b.start,
      endSec: b.end,
      playKey: playGenRef.current,
    };
    setPlayerMode('clip');
    setActiveSegment(next);
    if (previewAvailable) playerRef.current?.playNow(next);
  };

  const toggle = (id: string) => {
    setOrderedIds((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 1) return prev;
        return prev.filter((x) => x !== id);
      }
      if (prev.length >= maxClips) return prev;
      return insertChronologically(prev, id, byId);
    });
  };

  const clips = () => buildRefineClips(orderedIds, bounds, byId);
  const musicPayload = (): RefineMusicOptions | undefined =>
    musicEnabled ? music : undefined;
  const musicDisplayName =
    music.music_id && String(music.music_id).trim()
      ? musicTracks.find((t) => t.id === music.music_id)?.display_name ||
        music.music_id
      : null;
  const focusMusicPicker = () => {
    const panel = document.querySelector<HTMLElement>(
      '[data-testid="studio-music-panel"]'
    );
    panel?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    const select = document.querySelector<HTMLElement>(
      '[data-testid="studio-music-select"]'
    );
    select?.focus();
  };
  const cancelLabel = dirty
    ? STUDIO_COPY.refineCancelDiscard
    : STUDIO_COPY.refineCancel;
  const reelStale = dirty && Boolean(reelPreviewUrl) && !rebuildingReel;

  const rebuildPreview = () => {
    setPlayerMode('reel');
    setReelSource('watermarked');
    onSubmit(clips(), musicPayload());
  };

  const v2Instant = isRecapEditorV2Enabled();
  const instantVideoVolume = v2Instant
    ? music.music_mode === 'music_only' && music.music_id
      ? 0
      : volumePercentToLinear(
          mixVolumes.reel_audio_volume,
          DEFAULT_REEL_AUDIO_VOLUME
        )
    : !musicEnabled || !music.music_id
      ? 1
      : music.music_mode === 'music_only'
        ? 0
        : gainDbToLinearVolume(music.game_gain_db);
  const instantMusicVolume = v2Instant
    ? musicEnabled && music.music_id
      ? volumePercentToLinear(mixVolumes.music_volume, DEFAULT_MUSIC_VOLUME)
      : 0
    : musicEnabled && music.music_id
      ? gainDbToLinearVolume(music.music_gain_db)
      : 0;

  const onPlaybackTime = (sec: number, dur: number) => {
    setPlayheadSec(sec);
    setPlayheadDur(dur > 0 ? dur : totalDuration);
  };

  const seekPlayhead = (ratio: number) => {
    const dur = playheadDur > 0 ? playheadDur : totalDuration;
    const sec = Math.max(0, Math.min(dur, ratio * dur));
    setPlayheadSec(sec);
    if (dur > 0) setPlayheadDur(dur);
    if (playerMode === 'reel' && reelSource === 'watermarked') {
      adjustedPlayerRef.current?.seekTo(sec);
      return;
    }
    playerRef.current?.seekToReelTime(sec);
  };

  const clipClockOffset = activeSegment
    ? arrangementOffsetSec(orderedIds, bounds, activeSegment.id)
    : 0;

  const clipPlayer = (
    <ClipSegmentPlayer
      ref={playerRef}
      sourceUrl={sourcePreviewUrl}
      sourceExpiresAt={sourcePreviewExpiresAt}
      segment={activeSegment}
      onRefreshSource={onRefreshPreview}
      refreshingSource={refreshingPreview}
      showIdleHint={step === 1}
      onPlaybackTime={onPlaybackTime}
      reelClockOffsetSec={clipClockOffset}
      reelClockDurationSec={totalDuration}
    />
  );

  const instantReelPlayer = (
    <ClipSegmentPlayer
      ref={playerRef}
      sourceUrl={sourcePreviewUrl}
      sourceExpiresAt={sourcePreviewExpiresAt}
      segment={null}
      playlist={playlist}
      playlistPlayKey={playlistPlayKey}
      onRefreshSource={onRefreshPreview}
      refreshingSource={refreshingPreview}
      showIdleHint={false}
      title={STUDIO_COPY.customizeInstantTitle}
      subtitle={STUDIO_COPY.customizeInstantSubtitle}
      testId="studio-instant-reel-player"
      musicUrl={musicEnabled ? instantMusicUrl : null}
      videoVolume={instantVideoVolume}
      musicVolume={instantMusicVolume}
      musicLoop={Boolean(music.music_loop)}
      onPlaybackTime={onPlaybackTime}
    />
  );

  const adjustedPlayer = (
    <AdjustedReelPlayer
      ref={adjustedPlayerRef}
      reelUrl={reelPreviewUrl}
      rebuilding={rebuildingReel || submitting}
      error={reelPreviewError}
      stale={reelStale}
      remountKey={job?.updated_at || reelPreviewUrl || ''}
      variant="live"
      onPlaybackTime={onPlaybackTime}
    />
  );

  if (isRecapEditorV2Enabled()) {
    const activeClip = activeSegment?.id
      ? byId.get(activeSegment.id)
      : orderedIds[0]
        ? byId.get(orderedIds[0])
        : null;
    const activeBounds = activeClip
      ? bounds[activeClip.candidate_id]
      : null;
    const hasVoice = Boolean(job?.voice_asset_id || job?.voice_source);
    const watermarkOn = Boolean(job?.watermark_opt_in || job?.watermark);
    const displayName = reelName || job?.reel_name || STUDIO_COPY.reelsUntitled;
    const v2Stage =
      playerMode === 'reel' && reelSource === 'instant'
        ? instantReelPlayer
        : playerMode === 'clip'
          ? clipPlayer
          : adjustedPlayer;
    return (
      <RecapEditorV2
        reelName={displayName}
        onReelNameChange={onReelNameChange}
        clipCount={orderedIds.length}
        runtimeLabel={formatTotalClock(totalDuration)}
        accepted={job?.accepted}
        acceptedAt={job?.accepted_at}
        mixStatus={job?.mix_status}
        jobStatus={job?.status}
        dirty={dirty}
        musicLabel={musicDisplayName}
        hasVoice={hasVoice}
        watermarkOn={watermarkOn}
        previewBusy={busy}
        acceptBusy={acceptBusy}
        finalizeDisabled={
          !acceptEnabled ||
          !onApprove ||
          orderedIds.length < 1 ||
          overClipCap ||
          overDurationCap
        }
        acceptError={acceptError || error}
        acceptCostHint={acceptCostHint}
        onPreview={showInstantReel}
        onFinalize={() => {
          if (!onApprove) return;
          onApprove(clips(), { dirty }, musicPayload());
        }}
        onCancel={onCancel}
        cancelLabel={cancelLabel}
        recording={v2Recording}
        stage={
          <div
            className={`recap-v2-stage-inner${v2Recording ? ' is-recording' : ''}`}
            data-testid="recap-v2-stage-inner"
            data-recording={v2Recording ? 'true' : 'false'}
          >
            <span className="recap-v2-live-badge" data-testid="recap-v2-live-badge">
              {STUDIO_COPY.recapEditorV2LivePreview}
            </span>
            {v2Stage}
            <div
              ref={setV2RecordDock}
              className="recap-v2-record-dock"
              data-testid="recap-v2-record-dock"
            />
            <p className="recap-v2-hear-save" data-testid="recap-v2-hear-save">
              {RECAP_EDITOR_V2_PHASE0_MIX_CARRY
                ? STUDIO_COPY.recapEditorV2HearSave
                : STUDIO_COPY.customizeInstantSubtitle}
            </p>
          </div>
        }
        timeline={
          <CustomizeTimeline
            stackedLanes
            voiceLabel={hasVoice ? STUDIO_COPY.recapEditorV2VoiceOn : null}
            orderedIds={orderedIds}
            byId={byId}
            bounds={bounds}
            activeId={activeSegment?.id ?? null}
            disabled={busy}
            musicEnabled={musicEnabled}
            musicLabel={musicDisplayName}
            onMusicLaneClick={focusMusicPicker}
            playheadRatio={
              (playheadDur > 0 ? playheadDur : totalDuration) > 0
                ? playheadSec / (playheadDur > 0 ? playheadDur : totalDuration)
                : 0
            }
            onPlayheadSeek={seekPlayhead}
            musicFadeInS={music.music_fade_in_s}
            musicFadeOutS={music.music_fade_out_s}
            onMusicFadeChange={(next) => {
              setMusic((prev) => ({ ...prev, ...next }));
            }}
            onReorder={(next) => {
              setOrderedIds(next);
              if (playerMode === 'reel') setReelSource('instant');
            }}
            onBoundsChange={(id, next) => {
              setBounds((prev) => ({ ...prev, [id]: next }));
              setActiveSegment((seg) =>
                seg && seg.id === id
                  ? { ...seg, startSec: next.start, endSec: next.end }
                  : seg
              );
              if (playerMode === 'reel') setReelSource('instant');
            }}
            onSelectClip={playId}
          />
        }
        clipStrip={
          <RecapClipStrip
            orderedIds={orderedIds}
            byId={byId}
            bounds={bounds}
            activeId={activeSegment?.id ?? null}
            disabled={busy}
            onSelect={playId}
            onReorder={(next) => {
              setOrderedIds(next);
              if (playerMode === 'reel') setReelSource('instant');
            }}
            onBoundsChange={(id, next) => {
              setBounds((prev) => ({ ...prev, [id]: next }));
              setActiveSegment((seg) =>
                seg && seg.id === id
                  ? { ...seg, startSec: next.start, endSec: next.end }
                  : seg
              );
              if (playerMode === 'reel') setReelSource('instant');
            }}
          />
        }
        renderInspector={(tool) => (
          <>
            {tool === 'clips' ? (
              <div data-testid="recap-v2-inspector-clips">
                {activeClip && activeBounds ? (
                  <div
                    className="recap-v2-clip-detail"
                    data-testid="recap-v2-clip-detail"
                  >
                    <p className="recap-v2-clip-detail-label">{activeClip.label}</p>
                    <p className="recap-v2-clip-detail-meta">
                      {formatTotalClock(
                        Math.max(0, activeBounds.end - activeBounds.start)
                      )}
                    </p>
                    <div
                      className="flex flex-wrap gap-2"
                      data-testid="studio-customize-nudge"
                    >
                      <button
                        type="button"
                        className="studio-btn-ghost text-xs"
                        disabled={busy}
                        onClick={() => {
                          const next = clampBounds(
                            trimRails(activeClip),
                            activeBounds.start - 0.5,
                            activeBounds.end
                          );
                          setBounds((prev) => ({
                            ...prev,
                            [activeClip.candidate_id]: next,
                          }));
                        }}
                      >
                        {STUDIO_COPY.refineTrimStartEarlier}
                      </button>
                      <button
                        type="button"
                        className="studio-btn-ghost text-xs"
                        disabled={busy}
                        onClick={() => {
                          const next = clampBounds(
                            trimRails(activeClip),
                            activeBounds.start + 0.5,
                            activeBounds.end
                          );
                          setBounds((prev) => ({
                            ...prev,
                            [activeClip.candidate_id]: next,
                          }));
                        }}
                      >
                        {STUDIO_COPY.refineTrimStartLater}
                      </button>
                      <button
                        type="button"
                        className="studio-btn-ghost text-xs"
                        disabled={busy}
                        onClick={() => {
                          const next = clampBounds(
                            trimRails(activeClip),
                            activeBounds.start,
                            activeBounds.end - 0.5
                          );
                          setBounds((prev) => ({
                            ...prev,
                            [activeClip.candidate_id]: next,
                          }));
                        }}
                      >
                        {STUDIO_COPY.refineTrimEndEarlier}
                      </button>
                      <button
                        type="button"
                        className="studio-btn-ghost text-xs"
                        disabled={busy}
                        onClick={() => {
                          const next = clampBounds(
                            trimRails(activeClip),
                            activeBounds.start,
                            activeBounds.end + 0.5
                          );
                          setBounds((prev) => ({
                            ...prev,
                            [activeClip.candidate_id]: next,
                          }));
                        }}
                      >
                        {STUDIO_COPY.refineTrimEndLater}
                      </button>
                    </div>
                  </div>
                ) : null}
                <label className="recap-v2-toggle">
                  <input
                    type="checkbox"
                    checked={autoArrange}
                    data-testid="recap-v2-auto-arrange"
                    onChange={(event) => setAutoArrange(event.target.checked)}
                  />
                  {STUDIO_COPY.recapEditorV2AutoArrange}
                </label>
                <label className="recap-v2-toggle">
                  <input
                    type="checkbox"
                    checked={snap}
                    data-testid="recap-v2-snap"
                    onChange={(event) => setSnap(event.target.checked)}
                  />
                  {STUDIO_COPY.recapEditorV2Snap}
                </label>
              </div>
            ) : null}
            {tool === 'music' && musicEnabled ? (
              <CustomizeMusicPanel
                tracks={musicTracks}
                musicId={music.music_id}
                musicMode={music.music_mode as MusicMode}
                gameGainDb={music.game_gain_db}
                musicGainDb={music.music_gain_db}
                musicLoop={music.music_loop}
                musicFadeInS={music.music_fade_in_s}
                musicFadeOutS={music.music_fade_out_s}
                disabled={busy}
                hideGainSliders
                onChange={setMusic}
              />
            ) : null}
            {job && onJobUpdated && tool !== 'clips' && tool !== 'music' ? (
              <AddOnsPanel
                job={job}
                entitlement={entitlement}
                onJobUpdated={onJobUpdated}
                videoRef={v2VideoRef}
                recordDock={v2RecordDock || recordDock}
                onRecordingChange={(next) => {
                  setV2Recording(next);
                  onRecordingChange?.(next);
                }}
                onMixVolumesChange={(next) => {
                  setMixVolumes(next);
                  setMusic((prev) => ({
                    ...prev,
                    game_gain_db: volumePercentToGainDb(
                      next.reel_audio_volume,
                      DEFAULT_REEL_AUDIO_VOLUME
                    ),
                    music_gain_db: volumePercentToGainDb(
                      next.music_volume,
                      DEFAULT_MUSIC_VOLUME
                    ),
                  }));
                }}
                inspectorSection={
                  tool === 'voice' ? 'voice' : tool === 'branding' ? 'branding' : 'mix'
                }
              />
            ) : null}
          </>
        )}
      />
    );
  }

  return (
    <div
      className={`studio-customize-frame ${
        step === 1 ? 'is-step1' : 'is-step2'
      }`}
      data-testid="studio-customize-frame"
    >
      <CustomizeHowItWorks />
      <div
        className={`studio-customize-shell ${
          step === 1 ? 'is-step1' : 'is-step2'
        }`}
        data-testid="studio-customize"
      >
      <header className="studio-customize-header">
        <h1 className="studio-customize-headline">
          {STUDIO_COPY.customizeHeadline}
        </h1>
        <p
          className="studio-customize-helper"
          data-testid="studio-customize-stepper"
        >
          {STUDIO_COPY.customizeStepper}
        </p>
        <p
          className="studio-customize-helper"
          data-testid="studio-refine-no-dock"
        >
          {STUDIO_COPY.refineNoDockNote}
        </p>
        <p className="studio-customize-step-title">
          {step === 1
            ? STUDIO_COPY.customizeStep1Title
            : STUDIO_COPY.customizeStep2Title}
        </p>
        {step === 2 ? (
          <p
            className="studio-customize-helper"
            data-testid="studio-customize-step2-hint"
          >
            {STUDIO_COPY.customizeStep2Hint}
          </p>
        ) : null}
      </header>

      {step === 1 ? (
        <div
          className="studio-customize-layout studio-customize-step1"
          data-testid="studio-customize-step1"
        >
          <aside
            className="studio-customize-player-dock"
            data-testid="studio-customize-step1-player"
          >
            <div className="studio-customize-player-column">{clipPlayer}</div>
          </aside>
          <CustomizePickStep
            candidates={candidates}
            orderedIds={orderedIds}
            bounds={bounds}
            maxClips={maxClips}
            maxDurationSec={maxDurationSec}
            totalDuration={totalDuration}
            overClipCap={overClipCap}
            overDurationCap={overDurationCap}
            disabled={busy}
            previewAvailable={previewAvailable}
            activePreviewId={activeSegment?.id ?? null}
            onToggle={toggle}
            onPreview={previewCandidate}
            onRemove={(id) => {
              setOrderedIds((prev) =>
                prev.length <= 1 ? prev : prev.filter((x) => x !== id)
              );
            }}
            onNext={() => setStep(2)}
          />
        </div>
      ) : (
        <div className="studio-customize-layout studio-customize-step2">
          <aside
            className="studio-customize-player-dock"
            data-testid="studio-customize-players"
          >
            <div className="studio-customize-player-column">
              <p
                className="studio-customize-helper"
                data-testid="studio-customize-clip-play-hint"
              >
                {STUDIO_COPY.customizeClipPlayHint}
              </p>
              <div
                className="studio-customize-player-toggle"
                role="tablist"
                aria-label="Preview source"
                data-testid="studio-customize-player-toggle"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={playerMode === 'clip'}
                  className={playerMode === 'clip' ? 'is-active' : ''}
                  data-testid="studio-customize-player-tab-clip"
                  onClick={() => setPlayerMode('clip')}
                >
                  {STUDIO_COPY.customizePlayerClipTab}
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={playerMode === 'reel'}
                  className={playerMode === 'reel' ? 'is-active' : ''}
                  data-testid="studio-customize-player-tab-reel"
                  onClick={() => showInstantReel()}
                >
                  {STUDIO_COPY.customizePlayerReelTab}
                </button>
              </div>
              {playerMode === 'reel' && Boolean(reelPreviewUrl) ? (
                <div
                  className="studio-customize-reel-source-toggle"
                  role="tablist"
                  aria-label="Adjusted reel source"
                  data-testid="studio-customize-reel-source-toggle"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={reelSource === 'instant'}
                    className={reelSource === 'instant' ? 'is-active' : ''}
                    data-testid="studio-customize-reel-source-instant"
                    onClick={() => showInstantReel()}
                  >
                    {STUDIO_COPY.customizeInstantTab}
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={reelSource === 'watermarked'}
                    className={reelSource === 'watermarked' ? 'is-active' : ''}
                    data-testid="studio-customize-reel-source-watermarked"
                    disabled={rebuildingReel || submitting}
                    onClick={() => setReelSource('watermarked')}
                  >
                    {STUDIO_COPY.customizeWatermarkedTab}
                  </button>
                </div>
              ) : null}
              {playerMode === 'clip' ? (
                clipPlayer
              ) : reelSource === 'watermarked' ||
                rebuildingReel ||
                submitting ? (
                <AdjustedReelPlayer
                  reelUrl={reelPreviewUrl}
                  rebuilding={rebuildingReel || submitting}
                  error={reelPreviewError}
                  stale={reelStale}
                />
              ) : (
                instantReelPlayer
              )}
            </div>
          </aside>

          <div
            className="studio-customize-step2-body"
            data-testid="studio-customize-clips"
          >
            <CustomizeTimeline
              orderedIds={orderedIds}
              byId={byId}
              bounds={bounds}
              activeId={activeSegment?.id ?? null}
              disabled={busy}
              musicEnabled={musicEnabled}
              musicLabel={musicDisplayName}
              onMusicLaneClick={focusMusicPicker}
              onReorder={(next) => {
                setOrderedIds(next);
                if (playerMode === 'reel') setReelSource('instant');
              }}
              onBoundsChange={(id, next) => {
                setBounds((prev) => ({ ...prev, [id]: next }));
                setActiveSegment((seg) =>
                  seg && seg.id === id
                    ? { ...seg, startSec: next.start, endSec: next.end }
                    : seg
                );
                if (playerMode === 'reel') setReelSource('instant');
              }}
              onSelectClip={playId}
            />

            {musicEnabled ? (
              <CustomizeMusicPanel
                tracks={musicTracks}
                musicId={music.music_id}
                musicMode={music.music_mode as MusicMode}
                gameGainDb={music.game_gain_db}
                musicGainDb={music.music_gain_db}
                musicLoop={music.music_loop}
                musicFadeInS={music.music_fade_in_s}
                musicFadeOutS={music.music_fade_out_s}
                disabled={busy}
                onChange={setMusic}
              />
            ) : null}

            {reelStale ? (
              <p
                className="studio-customize-helper studio-customize-stale"
                data-testid="studio-customize-reel-stale"
              >
                {STUDIO_COPY.customizeReelStale}
              </p>
            ) : null}

            {overClipCap ? (
              <p className="text-sm text-[var(--studio-danger)]" role="alert">
                {STUDIO_COPY.customizeClipCap.replace('{max}', String(maxClips))}
              </p>
            ) : null}
            {overDurationCap ? (
              <p className="text-sm text-[var(--studio-danger)]" role="alert">
                {STUDIO_COPY.customizeDurationCap.replace(
                  '{max}',
                  String(Math.round(maxDurationSec))
                )}
              </p>
            ) : null}
            {error ? (
              <p className="text-sm text-[var(--studio-danger)]" role="alert">
                {error}
              </p>
            ) : null}
            {acceptError ? (
              <p
                className="text-sm text-[var(--studio-danger)]"
                role="alert"
                data-testid="studio-customize-accept-error"
              >
                {acceptError}
              </p>
            ) : null}

            <div
              className="studio-customize-action-bar"
              data-testid="studio-customize-action-bar"
            >
              <button
                type="button"
                className="studio-btn-primary studio-customize-action-primary"
                data-testid="studio-refine-submit"
                disabled={
                  busy ||
                  orderedIds.length < 1 ||
                  overClipCap ||
                  overDurationCap
                }
                onClick={rebuildPreview}
              >
                {submitting || rebuildingReel
                  ? STUDIO_COPY.refineBusy
                  : STUDIO_COPY.customizeSubmit}
              </button>

              {acceptEnabled && onApprove ? (
                <div className="studio-customize-approve-block">
                  {acceptCostHint ? (
                    <p
                      className="studio-customize-helper"
                      data-testid="studio-customize-accept-cost"
                    >
                      {acceptCostHint}
                    </p>
                  ) : null}
                  <p className="studio-customize-helper">
                    {STUDIO_COPY.acceptCleanHint}
                  </p>
                  <button
                    type="button"
                    className="studio-btn-primary studio-customize-action-primary"
                    data-testid="studio-customize-approve"
                    disabled={
                      busy ||
                      orderedIds.length < 1 ||
                      overClipCap ||
                      overDurationCap
                    }
                    onClick={() => onApprove(clips(), { dirty }, musicPayload())}
                  >
                    {acceptBusy
                      ? STUDIO_COPY.acceptCtaBusy
                      : submitting || rebuildingReel
                        ? STUDIO_COPY.refineBusy
                        : STUDIO_COPY.acceptCtaWithCost}
                  </button>
                </div>
              ) : null}

              <div className="studio-customize-action-secondary">
                <button
                  type="button"
                  className="studio-btn-ghost"
                  data-testid="studio-customize-back-pick"
                  disabled={busy}
                  onClick={() => setStep(1)}
                >
                  {STUDIO_COPY.customizeBackToPick}
                </button>
                <button
                  type="button"
                  className="studio-btn-ghost"
                  data-testid="studio-refine-cancel"
                  disabled={busy}
                  onClick={onCancel}
                >
                  {cancelLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default RefineReviewPanel;
